import {
  Component, ChangeDetectorRef, OnInit, OnDestroy, ViewChild, ViewChildren,
  QueryList,
} from '@angular/core';
import * as _ from 'lodash';


import { SegmentFieldItem } from 'utils/segment-field-item.interface';

import { ScorecardComponent } from 'shared/scorecard/scorecard/scorecard.component';
import { ScorecardItemComponent } from 'shared/scorecard/scorecard-item/scorecard-item.component';

import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import {takeWhile} from 'rxjs/operators';


@Component({
  template: '',
})
export class BaseScorecardComponent implements OnInit, OnDestroy {

  @ViewChild(ScorecardComponent, {static: false})
  scorecard: ScorecardComponent | void;

  @ViewChildren('scorecardItem')
  scorecardItems: QueryList<ScorecardItemComponent>;

  // data containing product information of all segments
  // loaded from BE
  itemDetail: any;

  // data containing score information of all segments
  // loaded from BE
  scoreData: any;

  scorecardData: any;

  // data containing information about current segment fields
  // stored on FE, define it in the child component
  segmentData: any;

  // e.g. Amazon Scorecard, eBay eCommerce Scorecard
  // comes from BE
  scorecardName: string;

  // title displaying in the scorecard (e.g. Amazon Detail Scorecard)
  // modifyed version of scorecardName
  scorecardComponentTitle: string;

  // title of the segment,
  // define in the Child component
  // used in scorecardComponentTitle
  segmentTitle: string;

  pathToScorecard: string;

  statusesMap: any = {
    'INVALID': 0,
    'VALID': 1,
  };

  isActive: boolean = true;

  defaultInvalidText: string = 'Invalid Entry';
  defaultMissingText: string = 'Field required';

  priceSheets: any;

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  // Scores object, define in the child
  get segmentScores(): any {
    return _.get(this.scoreData, `segments.${this.pathToScorecard}.tallies`);
  }

  get itemDetailValidation(): any {
    return _.get(this.itemDetail, 'validation', {});
  }

  get scorecardFields(): any {
    return _.get(this.scorecardData, `segments.${this.pathToScorecard}.fields`);
  }

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
  ) {}

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    this.setComponentTitle();

    this.scorecardConnectorService.controlStatusChanged
    .pipe(takeWhile(() => this.isActive))
    .subscribe((data: any) => {
      // {status: INVALID|VALID, item: any}
      this.handleControlStatusChange(data);
    });

    this.scorecardConnectorService.controlFocused
    .pipe(takeWhile(() => this.isActive))
    .subscribe((data: any) => {
      this.handleControlFocus(data);
    });

    this.scorecardConnectorService.controlRemoved
    .pipe(takeWhile(() => this.isActive))
    .subscribe((data: any) => {
      this.handleControlRemove(data);
    });

    this.scorecardConnectorService.controlAdded
    .pipe(takeWhile(() => this.isActive))
    .subscribe((data: any) => {
      this.handleControlAdd(data);
    });

    this.scorecardConnectorService.controlPopulatedChanged
    .pipe(takeWhile(() => this.isActive))
    .subscribe((data: any) => {
      this.handleControlPopulated(data);
    });

    this.scorecardConnectorService.detectChangesInScorecard
    .pipe(takeWhile(() => this.isActive))
    .subscribe((data: any) => {
      this.ref.markForCheck();
    });

    this.scorecardConnectorService.conditionalRequiredDataChanged
    .pipe(takeWhile(() => this.isActive))
    .subscribe((data: any) => {
      this.handleConditionalRequiredDataChanged(data);
    });

    _.defer(() => this.requireConditionalStatuses());
  }

  ngOnDestroy() {
    this.isActive = false;
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  public updateData(scoreData: any, scorecardName: string, itemDetail?: any, scorecardData?: any, priceSheets?: any): void {
    this.scoreData = scoreData;
    this.scorecardName = scorecardName;
    this.itemDetail = itemDetail;
    this.scorecardData = scorecardData;
    this.priceSheets = priceSheets;
    this.setComponentTitle();
    this.ref.markForCheck();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  /*
    * After expanding score details we should focus on the control in the segment
  */
  onScorecardItemExpand(item: any, index?: number|string, expanded?: boolean) {
    if (_.isNil(expanded)) { expanded = true; }

    if (_.isNil(index)) {
      this.scorecardConnectorService.scoreDetailsExpanded.emit({item, expanded});
    } else {
      this.scorecardConnectorService.scoreDetailsExpanded.emit({item, index, expanded});
    }
  }

  /*
    * After hovering score header we should highlight control in the segment
  */
  onScorecardHeaderHover(item: any, index?: number|string) {
    if (_.isNil(index)) {
      this.scorecardConnectorService.scorecardItemHovered.emit(item);
    } else {
      this.scorecardConnectorService.scorecardItemHovered.emit({item, index});
    }
  }

  /*
    * After bluring score header we should unhighlight control in the segment
  */
  onScorecardHeaderBlur(item: any, index?: number|string) {
    if (_.isNil(index)) {
      this.scorecardConnectorService.scorecardItemBlured.emit(item);
    } else {
      this.scorecardConnectorService.scorecardItemBlured.emit({item, index});
    }
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  /*
    * Update score status, update total scores count
    $event: {status: INVALID|VALID, item: any}
  */
  protected handleControlStatusChange($event?: any): void {
    const score: any = this.getItemFieldScoreFromEvent($event);
    if (!score) { return; }
    if (score.valid !== this.statusesMap[$event.status]) {
      score.valid = this.statusesMap[$event.status];
      this.scorecardConnectorService.totalScoreChanged.emit(score);
      this.ref.markForCheck();
    }
  }

  /*
    * Returns item's score data
    * Redefine in the Child
  */
  protected getItemFieldScoreFromEvent($event: any): any {
    return this.getItemFieldScoreByItemName($event.item);
  }

  /*
    * Returns item's score data by item_name
  */
  protected getItemFieldScoreByItemName(item: any): any {
    return _.get(this.segmentScores, item.item_name);
  }

  /*
    * Expands score details according on selected item
  */
  protected handleControlFocus(data: any) {}

  /*
    * Expands nested score details according on selected item
    * Common handler for expanding nested scores
  */
  protected handleNestedControlFocus($event: any) {
    const scorecardItem = this.findScorecardItemViaData($event);
    // collapse all scorecads, which are expanded now
    this.collapseExpandedScores($event.item.field_map);
    // scroll to active scorecard item
    if (this.scorecard instanceof ScorecardComponent) {
      this.scorecard.scrollToScorecardItem(scorecardItem);
    }
    // collapse child scorecards of the active scorecard item (languages ['en', 'es', 'fr'])
    this.collapseChildScoresViaData(scorecardItem, $event);
    _.defer(() => {
      // expand active scorecard item
      if (_.get(scorecardItem, 'collapsed')) { scorecardItem.collapseDetails(false); }
      // expand active child scorecard item (language in case of description segment)
      const childScorecardItem = this.getChildScorecardItemViaData(scorecardItem, $event);
      if (_.get(childScorecardItem, 'collapsed')) { childScorecardItem.collapseDetails(false); }
      this.ref.markForCheck();
    });
  }

  /*
    * Define in the child if it has nested scorecards
    * Used by handleNestedControlFocus, finds scorecard by data from event
  */
  protected findScorecardItemViaData(data: any): ScorecardItemComponent {
    return data;
  }
  /*
    * Define in the child if it has nested scorecards
    * Used by handleNestedControlFocus
  */
  protected collapseChildScoresViaData(scorecardItem: ScorecardItemComponent, data: any): void {}
  /*
    * Define in the child if it has nested scorecards
    * Used by handleNestedControlFocus
  */
  protected getChildScorecardItemViaData(scorecardItem: ScorecardItemComponent, data: any): ScorecardItemComponent {
    return scorecardItem;
  }

  /*
    * Updates populated value which affect on status icon
    * $event must have populated: 1|0 property
  */
  protected handleControlPopulated($event?: any): void {
    const score: any = this.getItemFieldScoreFromEvent($event);
    if (score && score.populated !== $event.populated) {
      score.populated = $event.populated;
      this.ref.markForCheck();
    }
  }

  protected setComponentTitle() {
    this.scorecardComponentTitle = this.getComponentTitle(this.segmentTitle);
  }

  protected getComponentTitle(segmentTitle: string) {
    const name = this.scorecardName.split('Scorecard')[0];
    return `${name} ${segmentTitle} Scorecard`;
  }

  /*
    * Update score related to the removed control
    * Define in the Child
   */
  protected handleControlRemove($event: any) {}

  /*
    * Add score related to the control
    * Define in the Child
   */
  protected handleControlAdd($event: any) {}

  protected handleScoresOrderChange($event: any) {
    this.ref.markForCheck();
  }

  /*
    * Handle conditionals 'required' changes - update in component

    format example:
    {
      'packages.EA.quantity': true,
    }
   */
  protected handleConditionalRequiredDataChanged(data: any) {
    _.forEach(_.keys(data), key => {
      const score = _.find(this.segmentScores, item => {
        return _.keys(item).indexOf(key) >= 0;
      });
      if (!score || !score[key]) {
        return;
      }
      score[key].rule.required = data[key];
    });
    this.ref.markForCheck();
  }

  protected getValidationMessage(item: SegmentFieldItem, score?: any): string {

    if (!score) { score = _.get(this.segmentScores, item.item_name); }
    const required = _.get(score, 'rule.required');
    const populated = _.get(score, 'populated');

    if (required && !populated) { return this.defaultMissingText; }
    let ruleName = _.get(score, 'rule.validation_rule');

    if (ruleName) {
      if (ruleName === 'pies67') {
          ruleName = 'pies65';
      }
      return _.get(item, `validation.${ruleName}.message`, this.defaultInvalidText);
    } else {
      return this.defaultInvalidText;
    }
  }

  /*
    * Find prevoiusly expanded items and collapse them
  */
  protected collapseExpandedScores(activeFieldMap: string): void {
    this.scorecardItems.forEach((scorecardItem: ScorecardItemComponent) => {
      if (scorecardItem.fieldMap !== activeFieldMap && !scorecardItem.collapsed) {
        scorecardItem.collapseDetails(true);
      }
    });
  }

  /*
    * Find prevoiusly expanded child scores and collapse them
  */
  protected collapseExpandedChildScores(childScorecardItems: QueryList<ScorecardItemComponent>, name: string): void {
    childScorecardItems.forEach((scorecardItem: ScorecardItemComponent) => {
      if (scorecardItem.name !== name && !scorecardItem.collapsed) {
        scorecardItem.collapseDetails(true);
      }
    });
  }

  protected getChildScorecardItemByName(scorecardItem: ScorecardItemComponent, name: string): ScorecardItemComponent {
    return scorecardItem.scorecardChildItems.find((_scorecardItem: ScorecardItemComponent) => {
      return _scorecardItem.name === name;
    });
  }

  protected findScorecardItem(scorecardProperty: string, value: any): ScorecardItemComponent {
    return this.scorecardItems.find((_scorecardItem: ScorecardItemComponent) => {
      return _scorecardItem[scorecardProperty] === value;
    });
  }

  protected getScorecardField(path: string): any {
    return _.find(this.scorecardFields, (item: any) => {
      return item.path.indexOf(path) !== -1;
    });
  }

  protected getScorecardFieldValidationRule(path: string): string {
    return _.get(this.getScorecardField(path), 'validation_rule');
  }

  protected getEmptyScoreItem(
    rule: string = '',
    path: string = '',
    score: number = 0,
    populated: number = 0,
    valid: number = 1,
    possible: number = 1,
    required: boolean = false,
   ): any {
    return {
      'path': path,
      'score': score,
      'populated': populated,
      'valid': valid,
      'possible': possible,
      'rule': {
        'validation_rule': rule,
        'required': required,
      },
    };
  }

  protected getEmptyScoreArrayItem(path: string): any {
    const scorecardField = this.getScorecardField(path);
    if (!scorecardField) { return this.getEmptyScoreItem(path); }

    const valid = Number(!scorecardField.required);

    return this.getEmptyScoreItem(
      this.getScorecardFieldValidationRule(path),
      path,
      valid,
      0,
      valid,
    );
  }

  protected isItemArray(item: any): boolean {
    return _.isArray(item);
  }

  protected requireConditionalStatuses(): void {
    this.scorecardConnectorService.requireConditionalStatuses.emit();
  }

}
